import { useEffect, useReducer, useRef } from 'react';
import type { TileConfig } from '../config/schema';

export type CatalogState =
  | { tag: 'loading' }
  | { tag: 'loaded'; tiles: TileConfig[] }
  | { tag: 'empty-no-results'; activeFilter: string }
  | { tag: 'empty-no-access' }
  | { tag: 'empty-fetch-failed'; errorCode?: string };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; tiles: TileConfig[] }
  | { type: 'FETCH_ERROR'; errorCode?: string }
  | { type: 'FILTER_EMPTY'; activeFilter: string }
  | { type: 'NO_ACCESS' };

const SKELETON_MIN_MS = 240;

function reducer(_state: CatalogState, action: Action): CatalogState {
  switch (action.type) {
    case 'FETCH_START':    return { tag: 'loading' };
    case 'FETCH_SUCCESS':  return { tag: 'loaded', tiles: action.tiles };
    case 'FETCH_ERROR':    return { tag: 'empty-fetch-failed', errorCode: action.errorCode };
    case 'FILTER_EMPTY':   return { tag: 'empty-no-results', activeFilter: action.activeFilter };
    case 'NO_ACCESS':      return { tag: 'empty-no-access' };
    default:               return _state;
  }
}

interface UseCatalogOptions {
  tiles: TileConfig[];
  activeCategory: string;
  search: string;
  /** Preview override — skip fetch, force a state */
  preview?: CatalogState['tag'];
}

export function useCatalog({ tiles, activeCategory, search, preview }: UseCatalogOptions) {
  const skeletonStart = useRef<number>(0);
  const [state, dispatch] = useReducer(reducer, { tag: 'loading' });

  useEffect(() => {
    if (preview) {
      switch (preview) {
        case 'loading':
          dispatch({ type: 'FETCH_START' }); return;
        case 'loaded':
          dispatch({ type: 'FETCH_SUCCESS', tiles }); return;
        case 'empty-no-results':
          dispatch({ type: 'FILTER_EMPTY', activeFilter: 'preview' }); return;
        case 'empty-no-access':
          dispatch({ type: 'NO_ACCESS' }); return;
        case 'empty-fetch-failed':
          dispatch({ type: 'FETCH_ERROR', errorCode: 'PREVIEW_ERROR' }); return;
      }
    }

    dispatch({ type: 'FETCH_START' });
    skeletonStart.current = Date.now();

    const delay = Math.max(0, SKELETON_MIN_MS - (Date.now() - skeletonStart.current));

    const timer = setTimeout(() => {
      // Filter tiles by category and search
      let filtered = tiles;
      if (activeCategory !== 'All') {
        filtered = filtered.filter((t) => t.tags.includes(activeCategory));
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (t) =>
            t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
        );
      }

      if (filtered.length === 0 && tiles.length > 0) {
        dispatch({ type: 'FILTER_EMPTY', activeFilter: activeCategory || search });
      } else if (tiles.length === 0) {
        dispatch({ type: 'NO_ACCESS' });
      } else {
        dispatch({ type: 'FETCH_SUCCESS', tiles: filtered });
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [tiles, activeCategory, search, preview]);

  return { state, retry: () => dispatch({ type: 'FETCH_START' }) };
}
